
import { useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { Message } from '@/components/ai/types';
import { useAuth } from '@/contexts/AuthContext';

interface UseChatContextProps {
  chatId: string;
  messages: Message[];
  session: any;
  setContextError: (error: boolean) => void;
}

export const useChatContext = ({ chatId, messages, session, setContextError }: UseChatContextProps) => {
  const { session: authSession } = useAuth();
  
  const { data: contextData, isLoading: isLoadingContext, error } = useQuery({
    queryKey: ['chat-context', chatId],
    queryFn: async () => {
      console.log("Fetching chat context for:", chatId);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return {
        id: chatId,
        context_id: chatId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        content: "Chat context content",
        metadata: {}
      };
    },
    enabled: !!authSession?.user,
  });

  // Mock updateHistory function
  const updateHistory = async (historyText: string) => {
    console.log('Updating chat history:', historyText);
    return Promise.resolve();
  };

  useEffect(() => {
    if (error) {
      console.error("Chat context error:", error);
      setContextError(true);
    } else if (contextData) {
      setContextError(false);
    }
  }, [error, contextData, setContextError]);

  useEffect(() => {
    if (authSession?.user?.id && contextData && messages.length > 0) {
      try {
        const historyText = messages.map(msg => 
          `${msg.sender === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
        ).join('\n\n');
        
        updateHistory(historyText)
          .catch(error => {
            console.error("Failed to update chat history:", error);
            setContextError(true);
          });
      } catch (error) {
        console.error("Error updating chat context:", error);
        setContextError(true);
      }
    }
  }, [messages, authSession?.user?.id, contextData, setContextError]);

  return { contextData, isLoadingContext };
};
